import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  WritableSignal,
  input,
  output
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, tap } from 'rxjs/operators';
import { CalendarModule } from '../calendar/calendar.module';
import { TimeRangePipe } from '~backoffice/app/~shared/pipes/time-range.pipe';
import { TimeRange, TimeRangeName } from '~core/models/shared.models';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DateService } from '~core/services/date.service';

@Component({
  selector: 'date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrl: './date-range-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    CommonModule,
    CalendarModule,
    TimeRangePipe,
    AngularSvgIconModule
  ]
})
export class DateRangeFilterComponent implements OnInit {
  // Optional Router and ActivatedRoute for query params sync
  private readonly _router = inject(Router, { optional: true });
  private readonly _activatedRoute = inject(ActivatedRoute, { optional: true });
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dateService = inject(DateService);

  initialTimeRangeName = input<TimeRangeName | null>(null);
  initialTimeRange = input<TimeRange | null>(null);
  syncWithQueryParams = input<boolean>(false);
  placeholder = input<string>('Période');

  timeRangeChange = output<{ name: TimeRangeName; range: TimeRange }>();
  dateTimeRangeChange = output<{
    startDateTime: string;
    endDateTime: string;
  }>();

  selectedTimeRangeName: WritableSignal<TimeRangeName | null> = signal(null);
  selectedTimeRange: WritableSignal<TimeRange | null> = signal(null);
  private _isUpdatingFromQueryParams = false;
  private _isInitializing = true;

  constructor() {
    effect(() => {
      const name = this.selectedTimeRangeName();
      const range = this.selectedTimeRange();

      if (
        name &&
        range &&
        !this._isUpdatingFromQueryParams &&
        !this._isInitializing
      ) {
        this.timeRangeChange.emit({ name, range });
        this.dateTimeRangeChange.emit({
          startDateTime: range.start,
          endDateTime: range.end
        });
      }
    });
  }

  ngOnInit(): void {
    // Initialize from inputs or query params
    if (this.syncWithQueryParams() && this._activatedRoute) {
      this._initializeFromQueryParams();
      this._subscribeToQueryParams();

      // If no query params were found, fall back to initialTimeRangeName
      if (!this.selectedTimeRangeName() && this.initialTimeRangeName()) {
        this._setTimeRangeFromName(this.initialTimeRangeName()!);
      }
    } else if (this.initialTimeRangeName() || this.initialTimeRange()) {
      // Initialize from inputs
      if (this.initialTimeRangeName()) {
        this._setTimeRangeFromName(this.initialTimeRangeName()!);
      }
      if (this.initialTimeRange()) {
        this.selectedTimeRange.set(this.initialTimeRange());
      }
    }

    this._isInitializing = false;
  }

  _handleTimeRangeChange(event: {
    name: TimeRangeName;
    range: TimeRange;
  }): void {
    this.selectedTimeRangeName.set(event.name);
    this.selectedTimeRange.set(event.range);

    // Sync with query params only when user selects a date
    if (
      this.syncWithQueryParams() &&
      this._router &&
      this._activatedRoute &&
      !this._isUpdatingFromQueryParams
    ) {
      this._updateQueryParams(event.name, event.range);
    }
  }

  private _subscribeToQueryParams(): void {
    if (!this._activatedRoute) {
      return;
    }

    this._activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map(
          (
            params
          ): {
            timeRangeName: TimeRangeName | undefined;
            startDateTime: string | undefined;
            endDateTime: string | undefined;
          } => ({
            timeRangeName: params['timeRangeName'] as TimeRangeName | undefined,
            startDateTime: params['startDateTime'] as string | undefined,
            endDateTime: params['endDateTime'] as string | undefined
          })
        ),
        filter(
          ({ timeRangeName: periodName }) =>
            !!periodName && Object.values(TimeRangeName).includes(periodName)
        ),
        tap(({ timeRangeName: periodName, startDateTime, endDateTime }) => {
          this._isUpdatingFromQueryParams = true;
          this.selectedTimeRangeName.set(periodName as TimeRangeName);

          if (startDateTime && endDateTime) {
            this.selectedTimeRange.set({
              start: startDateTime,
              end: endDateTime
            });
          } else {
            // Generate time range from name if dates are missing
            const range = this._dateService.generateTimeRange(
              periodName as TimeRangeName
            );
            this.selectedTimeRange.set(range);
          }
          this._isUpdatingFromQueryParams = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private _initializeFromQueryParams(): void {
    if (!this._activatedRoute) {
      return;
    }

    const queryParams = this._activatedRoute.snapshot.queryParams;
    const timeRangeName = queryParams['timeRangeName'] as
      | TimeRangeName
      | undefined;
    const startDateTime = queryParams['startDateTime'] as string | undefined;
    const endDateTime = queryParams['endDateTime'] as string | undefined;

    if (timeRangeName && Object.values(TimeRangeName).includes(timeRangeName)) {
      this.selectedTimeRangeName.set(timeRangeName);

      if (startDateTime && endDateTime) {
        this.selectedTimeRange.set({
          start: startDateTime,
          end: endDateTime
        });
      } else {
        // Generate time range from name if dates are missing
        const range = this._dateService.generateTimeRange(timeRangeName);
        this.selectedTimeRange.set(range);
      }
    }
  }

  private _setTimeRangeFromName(name: TimeRangeName): void {
    this.selectedTimeRangeName.set(name);
    const range = this._dateService.generateTimeRange(name);
    this.selectedTimeRange.set(range);
  }

  private _updateQueryParams(name: TimeRangeName, range: TimeRange): void {
    if (!this._router || !this._activatedRoute) {
      return;
    }

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        timeRangeName: name,
        startDateTime: range.start,
        endDateTime: range.end
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
